import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { toast } from "sonner";

interface UseOnboardingFormResult<T> {
  formData: T;
  updateField: (field: keyof T, value: any) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  loading: boolean;
  saving: boolean;
  /** True once the form has been submitted — pages should render read-only */
  isCompleted: boolean;
  /** True while editing a previously submitted form (lifts read-only) */
  isEditing: boolean;
  submittedAt: string | null;
  /** Unlock a completed form for editing/resubmission, back at step 1 */
  startEditing: () => void;
  saveProgress: () => Promise<void>;
  submit: () => Promise<void>;
  /** True when an HR admin is viewing a hire's submission (read-only preview) */
  isPreview: boolean;
}

interface UseOnboardingFormOptions {
  /**
   * When set, loads a specific onboarding request's submission (HR read-only
   * preview) via GET /api/onboarding/:id/forms/:formId/submission instead of
   * the caller's own portal data. All mutations become no-ops.
   */
  previewRequestId?: string | null;
}

/**
 * Shared load/save/submit logic for the onboarding pre-start forms. Talks to
 * the generic per-form endpoints (GET/POST /api/onboarding/my-portal/forms/:formId).
 */
export function useOnboardingForm<T extends Record<string, any>>(
  formId: string,
  initialData: T,
  options: UseOnboardingFormOptions = {}
): UseOnboardingFormResult<T> {
  const { previewRequestId } = options;
  const isPreview = !!previewRequestId;
  const navigate = useNavigate();
  const [formData, setFormData] = useState<T>(initialData);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const endpoint = isPreview
          ? `/api/onboarding/${previewRequestId}/forms/${formId}/submission`
          : `/api/onboarding/my-portal/forms/${formId}`;
        const res = await api.get<{
          success: boolean;
          data: {
            formData: Partial<T> & { lastSavedStep?: number };
            isCompleted: boolean;
            submittedAt: string | null;
          };
        }>(endpoint, {
          skipErrorToast: true,
        });

        if (res.success && res.data) {
          if (res.data.formData) {
            const { lastSavedStep, ...saved } = res.data.formData;
            setFormData((prev) => ({ ...prev, ...saved }));
            if (lastSavedStep && !res.data.isCompleted) {
              setCurrentStep(lastSavedStep);
            }
          }
          setIsCompleted(res.data.isCompleted);
          setSubmittedAt(res.data.submittedAt);
        }
      } catch (error) {
        console.error("Error loading form data:", error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [formId, previewRequestId]);

  const updateField = (field: keyof T, value: any) => {
    if (isPreview) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const save = async (completed: boolean) => {
    if (isPreview) return;
    setSaving(true);
    try {
      const res = await api.post<{ success: boolean; message: string }>(
        `/api/onboarding/my-portal/forms/${formId}`,
        {
          formData,
          step: currentStep,
          isCompleted: completed,
        }
      );

      if (res.success) {
        if (completed) {
          toast.success(
            isCompleted
              ? "Form resubmitted successfully!"
              : "Form submitted successfully!"
          );
          navigate("/my-onboarding?tab=forms");
        } else {
          toast.success("Progress saved");
        }
      }
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error(completed ? "Failed to submit form" : "Failed to save progress");
    } finally {
      setSaving(false);
    }
  };

  const saveProgress = () => save(false);
  const submit = () => save(true);

  const startEditing = () => {
    if (isPreview) return;
    setIsEditing(true);
    setCurrentStep(1);
  };

  return {
    formData,
    updateField,
    currentStep,
    setCurrentStep,
    loading,
    saving,
    isCompleted,
    isEditing,
    submittedAt,
    startEditing,
    saveProgress,
    submit,
    isPreview,
  };
}
